/**
 * Metric Thresholds Settings Component
 * Visual range-based threshold configuration for performance metrics
 * with draggable sliders for intuitive adjustment
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { RotateCcw, Palette, GripVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from '@/components/ui/glass-card'
import { useMetricThresholds } from '@/hooks/useMetricThresholds'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/utils/cn'
import type { MetricThresholdsConfig } from '@/config'

// Only show metrics that are ACTUALLY used for color coding in the UI
// Excludes 'sharpe' (uses sortino thresholds) and 'objectiveScore' (not color coded)
const METRIC_CONFIG = {
  sortino: {
    label: 'Risk-Adjusted Return',
    description: 'Sortino ratio — measures return per unit of downside risk. Used for Sharpe/Sortino displays.',
    unit: '',
    step: 0.1,
    displayMin: 0,
    displayMax: 3,
    inverted: false,
    context: 'P75 = 1.48, P25 = 0.50 from 2,326 production trials',
  },
  winRate: {
    label: 'Win Rate',
    description: 'Percentage of trades that were profitable.',
    unit: '%',
    step: 1,
    displayMin: 30,
    displayMax: 70,
    inverted: false,
    context: 'P75 = 57.6%, P25 = 44.4% from production data',
  },
  drawdown: {
    label: 'Max Drawdown',
    description: 'Largest peak-to-trough portfolio decline. Lower is better.',
    unit: '%',
    step: 0.1,
    displayMin: 0,
    displayMax: 3,
    inverted: true,
    context: 'P25 = 0.60%, P75 = 1.18% from production data',
  },
  trades: {
    label: 'Trade Count',
    description: 'Number of trades — more trades = more statistical confidence.',
    unit: '',
    step: 1,
    displayMin: 0,
    displayMax: 50,
    inverted: false,
    context: '15+ trades = 16% chance of good strategy, <10 = insufficient data',
  },
  profitFactor: {
    label: 'Profit Factor',
    description: 'Gross profit ÷ gross loss. Values >1 are profitable, >2 is good.',
    unit: '',
    step: 0.1,
    displayMin: 0,
    displayMax: 6,
    inverted: false,
    context: 'P75 = 3.56, P25 = 1.86 from production data',
  },
} as const

type MetricKey = keyof typeof METRIC_CONFIG

interface MetricThresholdRowProps {
  config: typeof METRIC_CONFIG[MetricKey]
  greenValue: number
  yellowValue: number
  onGreenChange: (value: number) => void
  onYellowChange: (value: number) => void
}

function MetricThresholdRow({
  config,
  greenValue,
  yellowValue,
  onGreenChange,
  onYellowChange,
}: MetricThresholdRowProps) {
  const barRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'green' | 'yellow' | null>(null)
  const [editingValue, setEditingValue] = useState<'green' | 'yellow' | null>(null)
  const [editText, setEditText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const isInverted = config.inverted
  const range = config.displayMax - config.displayMin

  const getPosition = useCallback((value: number) => {
    const clamped = Math.max(config.displayMin, Math.min(config.displayMax, value))
    return ((clamped - config.displayMin) / range) * 100
  }, [config.displayMin, config.displayMax, range])

  const getValueFromPosition = useCallback((clientX: number) => {
    if (!barRef.current) return null
    const rect = barRef.current.getBoundingClientRect()
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
    const value = config.displayMin + (percent / 100) * range
    // Round to step
    return Math.round(value / config.step) * config.step
  }, [config.displayMin, config.step, range])

  const greenPos = getPosition(greenValue)
  const yellowPos = getPosition(yellowValue)

  // Format display value
  const formatValue = (v: number) => {
    if (config.step < 1) {
      return v.toFixed(1)
    }
    return v.toString()
  }

  // Handle dragging
  useEffect(() => {
    if (!dragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const value = getValueFromPosition(e.clientX)
      if (value === null) return

      if (dragging === 'green') {
        // For normal metrics, green must be >= yellow
        // For inverted metrics, green must be <= yellow
        if (isInverted) {
          onGreenChange(Math.min(value, yellowValue))
        } else {
          onGreenChange(Math.max(value, yellowValue))
        }
      } else {
        // For normal metrics, yellow must be <= green
        // For inverted metrics, yellow must be >= green
        if (isInverted) {
          onYellowChange(Math.max(value, greenValue))
        } else {
          onYellowChange(Math.min(value, greenValue))
        }
      }
    }

    const handleMouseUp = () => {
      setDragging(null)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragging, getValueFromPosition, greenValue, yellowValue, isInverted, onGreenChange, onYellowChange])

  // Handle inline editing
  const startEditing = (type: 'green' | 'yellow') => {
    setEditingValue(type)
    setEditText(formatValue(type === 'green' ? greenValue : yellowValue))
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const finishEditing = () => {
    if (editingValue) {
      const value = parseFloat(editText)
      if (!isNaN(value)) {
        if (editingValue === 'green') {
          onGreenChange(value)
        } else {
          onYellowChange(value)
        }
      }
    }
    setEditingValue(null)
    setEditText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishEditing()
    } else if (e.key === 'Escape') {
      setEditingValue(null)
      setEditText('')
    }
  }

  // Render editable value
  const EditableValue = ({ type, value }: { type: 'green' | 'yellow', value: number }) => {
    const isEditing = editingValue === type
    const colorClass = type === 'green' ? 'text-green-400' : 'text-yellow-400'

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={finishEditing}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-16 px-1 py-0.5 text-sm font-medium bg-dark-800 border border-dark-600 rounded text-center focus:outline-none focus:ring-1",
            type === 'green' ? "focus:ring-green-500" : "focus:ring-yellow-500",
            colorClass
          )}
          autoFocus
        />
      )
    }

    return (
      <button
        onClick={() => startEditing(type)}
        className={cn(
          "px-2 py-0.5 text-sm font-semibold rounded hover:bg-dark-700/50 transition-colors cursor-text",
          colorClass
        )}
        title="Click to edit"
      >
        {formatValue(value)}{config.unit}
      </button>
    )
  }

  return (
    <div className="py-5 border-b border-border/30 last:border-0">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-medium text-white">{config.label}</h4>
          <span className="text-[10px] text-dark-500 font-mono">{config.context}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {config.description}
        </p>
      </div>

      {/* Visual range bar with draggable handles */}
      <div
        ref={barRef}
        className="relative h-10 mb-4 cursor-crosshair select-none"
      >
        {/* Background gradient bar */}
        <div className="absolute inset-0 rounded-lg overflow-hidden flex">
          {isInverted ? (
            // Inverted: Green | Yellow | Red (left to right)
            <>
              <div
                className="bg-gradient-to-r from-green-600 to-green-500 h-full transition-all"
                style={{ width: `${greenPos}%` }}
              />
              <div
                className="bg-gradient-to-r from-yellow-500 to-yellow-500 h-full transition-all"
                style={{ width: `${yellowPos - greenPos}%` }}
              />
              <div className="bg-gradient-to-r from-red-500 to-red-600 h-full flex-1" />
            </>
          ) : (
            // Normal: Red | Yellow | Green (left to right)
            <>
              <div
                className="bg-gradient-to-r from-red-600 to-red-500 h-full transition-all"
                style={{ width: `${yellowPos}%` }}
              />
              <div
                className="bg-gradient-to-r from-yellow-500 to-yellow-500 h-full transition-all"
                style={{ width: `${greenPos - yellowPos}%` }}
              />
              <div className="bg-gradient-to-r from-green-500 to-green-600 h-full flex-1" />
            </>
          )}
        </div>

        {/* Scale markers */}
        <div className="absolute inset-x-0 -bottom-4 flex justify-between text-[10px] text-dark-500 pointer-events-none">
          <span>{config.displayMin}{config.unit}</span>
          <span>{config.displayMax}{config.unit}</span>
        </div>

        {/* Draggable threshold handles */}
        {isInverted ? (
          <>
            {/* Green handle (left side for inverted) */}
            <div
              className={cn(
                "absolute top-0 bottom-0 w-6 -ml-3 cursor-ew-resize group z-10",
                dragging === 'green' && "z-20"
              )}
              style={{ left: `${greenPos}%` }}
              onMouseDown={(e) => { e.preventDefault(); setDragging('green') }}
            >
              <div className={cn(
                "absolute inset-y-0 left-1/2 w-1 -ml-0.5 rounded-full transition-all",
                dragging === 'green' ? "bg-green-300 shadow-lg shadow-green-500/50" : "bg-white group-hover:bg-green-300"
              )} />
              <div className={cn(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                dragging === 'green'
                  ? "bg-green-500 border-green-300 scale-110"
                  : "bg-white border-green-500 group-hover:bg-green-100"
              )}>
                <GripVertical className="h-3 w-3 text-green-600" />
              </div>
            </div>
            {/* Yellow handle */}
            <div
              className={cn(
                "absolute top-0 bottom-0 w-6 -ml-3 cursor-ew-resize group z-10",
                dragging === 'yellow' && "z-20"
              )}
              style={{ left: `${yellowPos}%` }}
              onMouseDown={(e) => { e.preventDefault(); setDragging('yellow') }}
            >
              <div className={cn(
                "absolute inset-y-0 left-1/2 w-1 -ml-0.5 rounded-full transition-all",
                dragging === 'yellow' ? "bg-yellow-300 shadow-lg shadow-yellow-500/50" : "bg-white group-hover:bg-yellow-300"
              )} />
              <div className={cn(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                dragging === 'yellow'
                  ? "bg-yellow-500 border-yellow-300 scale-110"
                  : "bg-white border-yellow-500 group-hover:bg-yellow-100"
              )}>
                <GripVertical className="h-3 w-3 text-yellow-600" />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Yellow handle (left side for normal) */}
            <div
              className={cn(
                "absolute top-0 bottom-0 w-6 -ml-3 cursor-ew-resize group z-10",
                dragging === 'yellow' && "z-20"
              )}
              style={{ left: `${yellowPos}%` }}
              onMouseDown={(e) => { e.preventDefault(); setDragging('yellow') }}
            >
              <div className={cn(
                "absolute inset-y-0 left-1/2 w-1 -ml-0.5 rounded-full transition-all",
                dragging === 'yellow' ? "bg-yellow-300 shadow-lg shadow-yellow-500/50" : "bg-white group-hover:bg-yellow-300"
              )} />
              <div className={cn(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                dragging === 'yellow'
                  ? "bg-yellow-500 border-yellow-300 scale-110"
                  : "bg-white border-yellow-500 group-hover:bg-yellow-100"
              )}>
                <GripVertical className="h-3 w-3 text-yellow-600" />
              </div>
            </div>
            {/* Green handle */}
            <div
              className={cn(
                "absolute top-0 bottom-0 w-6 -ml-3 cursor-ew-resize group z-10",
                dragging === 'green' && "z-20"
              )}
              style={{ left: `${greenPos}%` }}
              onMouseDown={(e) => { e.preventDefault(); setDragging('green') }}
            >
              <div className={cn(
                "absolute inset-y-0 left-1/2 w-1 -ml-0.5 rounded-full transition-all",
                dragging === 'green' ? "bg-green-300 shadow-lg shadow-green-500/50" : "bg-white group-hover:bg-green-300"
              )} />
              <div className={cn(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                dragging === 'green'
                  ? "bg-green-500 border-green-300 scale-110"
                  : "bg-white border-green-500 group-hover:bg-green-100"
              )}>
                <GripVertical className="h-3 w-3 text-green-600" />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Threshold labels with click-to-edit values - order matches bar left-to-right */}
      <div className="grid grid-cols-3 gap-2 text-xs mt-6">
        {isInverted ? (
          // Inverted layout (drawdown): bar is Green | Yellow | Red left-to-right
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
              <span className="text-muted-foreground">Green:</span>
              <EditableValue type="green" value={greenValue} />
              <span className="text-dark-500">or less</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0" />
              <span className="text-muted-foreground">Yellow:</span>
              <span className="text-dark-400">
                <EditableValue type="green" value={greenValue} /> – <EditableValue type="yellow" value={yellowValue} />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
              <span className="text-muted-foreground">Red:</span>
              <span className="text-dark-400">above</span>
              <EditableValue type="yellow" value={yellowValue} />
            </div>
          </>
        ) : (
          // Normal layout: bar is Red | Yellow | Green left-to-right
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
              <span className="text-muted-foreground">Red:</span>
              <span className="text-dark-400">below</span>
              <EditableValue type="yellow" value={yellowValue} />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0" />
              <span className="text-muted-foreground">Yellow:</span>
              <span className="text-dark-400">
                <EditableValue type="yellow" value={yellowValue} /> – <EditableValue type="green" value={greenValue} />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
              <span className="text-muted-foreground">Green:</span>
              <EditableValue type="green" value={greenValue} />
              <span className="text-dark-500">or more</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function MetricThresholdsSettings() {
  const { thresholds, updateMetricThreshold, resetToDefaults, isCustomized, defaults } = useMetricThresholds()
  const { toast } = useToast()
  const [localThresholds, setLocalThresholds] = useState<MetricThresholdsConfig>(thresholds)

  const handleGreenChange = (metric: MetricKey, value: number) => {
    setLocalThresholds(prev => ({
      ...prev,
      [metric]: { ...prev[metric], green: value }
    }))
    updateMetricThreshold(metric, { green: value })
  }

  const handleYellowChange = (metric: MetricKey, value: number) => {
    setLocalThresholds(prev => ({
      ...prev,
      [metric]: { ...prev[metric], yellow: value }
    }))
    updateMetricThreshold(metric, { yellow: value })
  }

  const handleReset = () => {
    resetToDefaults()
    setLocalThresholds(defaults)
    toast({
      title: 'Thresholds reset',
      description: 'Color thresholds have been reset to data-driven defaults.',
    })
  }

  return (
    <GlassCard>
      <GlassCardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <Palette className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <GlassCardTitle className="text-lg">Metric Color Thresholds</GlassCardTitle>
              <GlassCardDescription>
                Drag the handles or click values to customize performance indicators
              </GlassCardDescription>
            </div>
          </div>
          {isCustomized && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </Button>
          )}
        </div>
      </GlassCardHeader>
      <GlassCardContent>
        <div className="space-y-0">
          {(Object.keys(METRIC_CONFIG) as MetricKey[]).map((metric) => (
            <MetricThresholdRow
              key={metric}
              config={METRIC_CONFIG[metric]}
              greenValue={localThresholds[metric].green}
              yellowValue={localThresholds[metric].yellow}
              onGreenChange={(value) => handleGreenChange(metric, value)}
              onYellowChange={(value) => handleYellowChange(metric, value)}
            />
          ))}
        </div>
      </GlassCardContent>
    </GlassCard>
  )
}